import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { Box, Grid, Typography } from "@mui/material"
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import moment from "moment";
import axios from "axios";
import BaseButton from "../BaseButton"
// import BaseCheckbox from "../BaseCheckbox";
import BaseSwitch from "../BaseSwitch";
import BaseInput from "../BaseInput";
import BaseColorText from "../BaseColorText";
import BaseImage from "../BaseImage";
import BirthInputGroup from "../BirthInputGroup";
import MedicationSelectForm from "../MedicationSelectForm";
// import FamilyGroupCheckboxList from "../FamilyGroupCheckboxList";
import { GENDERS, GENDERS_ENUM } from "../../constants";
import { validationsPatientDetailEditFormSchema } from "../../validations/validationSignUpSchema";
import { showErrorToast, showToast } from "../../utils/toast";
import { UpdateUser, UploadUserAvatar, UserSetMedications, VerifyUpload } from "../../apollo-client/api-query";
import { useMutation } from "@apollo/client";
import { refetchMe } from '../../store';
import { formatPhoneNumber } from "../../hooks/use-format-phone-number";
import PhoneNumberInput from "../PhoneNumberInput";
import { findMemberByUserId } from "../../utils/member-lookup";

const ProfileEditForm = ({ sx, onClose }) => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const members = useSelector(state => state.members)
  const selectedPatient = useMemo(() => findMemberByUserId(members, id), [members, id])
  // const reports = useSelector(state => state.reports)
  // const medicationData = useMemo(() => reports?.find(item => item.name === 'Medications'), [reports])
  const userMedications = useMemo(() => selectedPatient?.user?.medications, [selectedPatient])

  const [setMedications] = useMutation(UserSetMedications, {fetchPolicy: 'network-only'})

  const [updateUser, { loading }] = useMutation(UpdateUser)
  const [uploadUserAvatar] = useMutation(UploadUserAvatar)
  const [verifyUpload] = useMutation(VerifyUpload)

  const [selectedGender, setSelectedGender] = useState(GENDERS_ENUM.MALE)
  const [showMedicationSearchInput, setShowMedicationSearchInput] = useState(false)
  const [currentMedications, setCurrentMedications] = useState([])

  // const [file, setFile] = useState(null);
  // const [fileDataURL, setFileDataURL] = useState(null);
  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => setCurrentMedications(userMedications), [userMedications])

  const handleUploadFile = (e) => {
    setIsUploading(true)
    const [file] = e.target.files

    // if (!file.type.match(imageMimeType)) {
    //   alert("Image mime type is not valid");
    //   return;
    // }
    // setFile(file);
    // setFileDataURL(URL.createObjectURL(e.target.files[0]))

    uploadUserAvatar({
      variables: {
        uploadInput: {
          filename: file?.name,
          mimeType: file?.type,
          size: file?.size
        }
      }
    })
    .then(async ({ data }) => {
      const headersArray = data?.userUploadAvatar?.headers.map((({ key, value }) => ({ [key]: value }))),
      headersObj = Object.assign({}, ...headersArray)

      const headers = {
        'X-Requested-With': '*',
        'Access-Control-Allow-Headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,x-requested-with',
        'Access-Control-Allow-Origin': '*',
        ...headersObj
      }
      
      // upload file to presigned aws s3 bucket
      await axios({
        method: 'PUT',
        url: data?.userUploadAvatar?.url,
        data: file,
        headers
      })
      
      await verifyUpload({
        variables: {
          uploadId: data?.userUploadAvatar?.id
        }
      })
      setUploadedAvatarUrl(`https://dev.descendantsdna.com/public_files/${data?.userUploadAvatar?.id}`)
      dispatch(
        refetchMe(true)
      )
    })
    .catch(error => {
      showErrorToast({
        message: error?.message
      })
    })
    .finally(() => {
      setIsUploading(false)
    })
  }

  const { errors, handleBlur, handleChange, handleSubmit, touched, values, setValues } =
    useFormik({
      validateOnBlur: true,
      initialValues: {
        email: '',
        firstName: '',
        lastName: '',
        birthMonth: '',
        birthDate: '',
        birthYear: '',
        sex: GENDERS[0].title,
        phone: '',
      },
      validationSchema: validationsPatientDetailEditFormSchema,
      onSubmit: (values, { setSubmitting }) => {
        setSubmitting(true);
        const sex = selectedGender === 'MALE' ? GENDERS_ENUM.MALE : GENDERS_ENUM.FEMALE
        updateUser({
          variables: {
            user: {
              id,
              firstName: values.firstName,
              lastName: values.lastName,
              middleName: null,
              phone: values.phone,
              sex,
              dateOfBirth: moment(`${values.birthYear}-${values.birthMonth}-${values.birthDate}`).format('YYYY-MM-DD'),
            }
          }
        })
        .then(() => {
          dispatch(
            refetchMe(true)
          )
          onClose()
          return showToast({
            message: 'Profile updated'
          })
        })
        .catch((error) => {
          return showErrorToast({
            message: error.message
          })
        })
        .finally(() => {
          setSubmitting(false)
        })
      },
    });

    useMemo(() => {
      if (selectedPatient) {
        const dateOfBirth = moment(selectedPatient?.user?.dateOfBirth, 'YYYY/MM/DD')
        setValues({
          firstName: selectedPatient?.user?.firstName,
          lastName: selectedPatient?.user?.lastName,
          email: selectedPatient?.user?.email,
          birthMonth: dateOfBirth.format('MM'),
          birthDate: dateOfBirth.format('DD'),
          birthYear: dateOfBirth.format('YYYY'),
          phone: selectedPatient?.user?.phone ? formatPhoneNumber(selectedPatient?.user?.phone) : ''
        })
        setSelectedGender(selectedPatient?.user?.sex)
        setUploadedAvatarUrl(selectedPatient?.user?.avatarUrl)
      }
    }, [selectedPatient, setValues])
  
  const handleChangePhone = (inputValue) => {
    const formattedValue = formatPhoneNumber(inputValue)
    setValues({
      ...values,
      phone: formattedValue
    })
  }

  const handleRemoveCurrentMedication = (medication) => {
    const selectedMedications = currentMedications.filter(item => medication.name !== item.name)
    setCurrentMedications(selectedMedications)

    setMedications({
      variables: {
        userId: id,
        medications: selectedMedications.map(item => ({ name: item.name, strength: item.strength }))
      }
    }).catch(error => {
      showErrorToast({
        message: error.message
      })
    }).then(({ data }) => {
      dispatch(refetchMe(true))
    })
  }
  
  const handleMedicationSelectFormClose = () => {
    setShowMedicationSearchInput(false)
  }

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <Box
          className='notification-dropdown-scroll'
          sx={{
            width: '100%',
            height: { xs: '50vh', md: '60vh' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            padding: '10px 10px 16px 16px',
            mt: '20px',
            ...sx
          }}
        >
          <Box
            sx={{
              width: '100%',
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0px 2px 10px 0px #5B87EF33'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'primary.mediumGray',
                pb: '16px'
              }}
            >
              <Typography
                variant="h4"
              >
                Your Profile
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: '16px'
              }}
            >
              <BaseImage
                sx={{
                  width: '80px',
                  minWidth: '80px',
                  height: '80px',
                }}
                sxImage={{
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
                src={uploadedAvatarUrl || '//ssl.gstatic.com/accounts/ui/avatar_2x.png'}
                alt='profile-avatar'
              />
              <BaseButton
                buttonType={'default'}
                sx={{
                  maxWidth: '200px',
                  height: '31px',
                  ml: '16px'
                }}
                loading={isUploading}
              >
                <label htmlFor="upload-image">
                  <Typography variant="subText">
                    Change profile picture
                  </Typography>
                </label>
                <input
                  type="file"
                  id="upload-image"
                  onChange={handleUploadFile}
                  accept="image/*"
                  style={{
                    display: 'none'
                  }}
                />
              </BaseButton>
            </Box>
            {/* <form onSubmit={handleSubmit}> */}
              <BaseInput
                sx={{
                  mt: '16px',
                }}
                fullWidth
                label='Email'
                name='email'
                type='email'
                placeholder='Enter your email'
                value={values.email || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.email ? errors.email : ""}
                error={touched.email && Boolean(errors.email)}
                disabled
              />
              <BaseInput
                sx={{
                  mt: '8px',
                }}
                fullWidth
                label='First name'
                name='firstName'
                type='text'
                placeholder='Enter your first name'
                value={values.firstName || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.firstName ? errors.firstName : ""}
                error={touched.firstName && Boolean(errors.firstName)}
                required
              />
              <BaseInput
                sx={{
                  mt: '8px',
                }}
                fullWidth
                label='Last name'
                name='lastName'
                type='text'
                placeholder='Enter your last name'
                value={values.lastName || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.lastName ? errors.lastName : ""}
                error={touched.lastName && Boolean(errors.lastName)}
                required
              />
              <PhoneNumberInput
                sx={{
                  mt: '8px',
                }}
                fullWidth
                label='Phone number'
                isHelpIcon
                tooltipText={`Why does my phone number help? \n\n We use your phone number to make sure your concierge experience is as adaptive and helpful as possible.`}
                name='phone'
                type='text'
                placeholder='Phone'
                value={values.phone || ''}
                onChange={handleChangePhone}
                onBlur={handleBlur}
                helperText={touched.phone ? errors.phone : ""}
                error={touched.phone && Boolean(errors.phone)}
                required
              />
              {/* <BaseInput
                sx={{
                  mt: '8px',
                }}
                fullWidth
                label='Your phone number'
                isHelpIcon
                tooltipText={`Why does my phone number help? \n\n We use your phone number to make sure your concierge experience is as adaptive and helpful as possible.`}
                name='phone'
                type='text'
                placeholder='Phone'
                value={values.phone || ''}
                onChange={handleChangePhone}
                onBlur={handleBlur}
                helperText={touched.phone ? errors.phone : ""}
                error={touched.phone && Boolean(errors.phone)}
                required
              /> */}
              <BirthInputGroup
                sx={{
                  mt: '8px'
                }}
                values={values}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
              <Box sx={{ mt: '16px' }}>
                <Typography 
                  sx={{
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#46545F'
                  }}
                >
                  Assigned sex at birth <BaseColorText color='#E73E3E'>*</BaseColorText>
                </Typography>
                <BaseSwitch
                  sx={{ mt: '4px' }}
                  selected={selectedGender}
                  onChange={(value) => setSelectedGender(value)}
                  options={GENDERS}
                  activeColor={selectedGender === 'MALE' ? '#39B195' : '#E73E3E'}
                />
              </Box>
              {/* <BaseButton
                type="submit"
                variant='contained'
                buttonType='primary'
                width={{ xs: '100%', md: '160px' }}
                sx={{
                  // position: 'absolute',
                  // bottom: '16px',
                  // left: '16px'
                }}
                loading={isSubmitting}
              >
                <Typography
                  variant="body"
                  color="white"
                >
                  Save
                </Typography>
              </BaseButton>
            </form> */}
          </Box>
          {/* <Box
            sx={{
              width: '100%',
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0px 2px 10px 0px #5B87EF33',
              mt: '24px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderBottom: '1px solid',
                borderColor: 'primary.mediumGray',
                pb: '16px'
              }}
            >
              <Typography
                variant="h4"
              >
                Reports
              </Typography>
              <Typography variant="subText">
                Select reports that you would not want to set for the public (family members) to view without the administrator’s permission.
              </Typography>
            </Box>
            <Grid container spacing={'0px'}>
              {reports.map((item => (
                <Grid item xs={12} key={item.name}>
                  <Box
                    sx={{
                      padding: '0px 16px',
                      borderRadius: '8px',
                      bgcolor: 'background.default'
                    }}
                  >
                    <BaseCheckbox
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <BaseImage 
                            src={item.icon}
                            sx={{
                              width: '24px',
                              height: '24px',
                              mr: '12px'
                            }}
                            alt={item.name}
                          />
                          <Typography variant="body">
                            {item.name}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Grid>
              )))}
            </Grid>
          </Box> */}
          <Box
            sx={{
              width: '100%',
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0px 2px 10px 0px #5B87EF33',
              mt: '24px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderBottom: '1px solid',
                borderColor: 'primary.mediumGray',
                pb: '16px'
              }}
            >
              <Typography
                variant="h4"
              >
                Your current medication
              </Typography>
            </Box>
            <Box
              sx={{
                mt: '16px'
              }}
            >
              <Grid container spacing={'8px'}>
                {currentMedications.map((item => (
                  <Grid item xs={12} key={item.name}>
                    <Box
                      sx={{
                        padding: '12px',
                        borderRadius: '5px',
                        backgroundColor: 'primary.lightBlue',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <CloseIcon 
                          sx={{
                            fontSize: '24px',
                            color: 'primary.black',
                            mr: '8px',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleRemoveCurrentMedication(item)}
                        />
                        <Typography variant="body">{item.name}</Typography>
                      </Box>
                      <Typography variant="body" textAlign={'right'}>{item.strength}</Typography>
                    </Box>
                  </Grid>
                )))}
              </Grid>
              {!showMedicationSearchInput ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '10px 16px',
                    mt: '16px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowMedicationSearchInput(true)}
                >
                  <AddIcon 
                    sx={{
                      fontSize: '20px',
                      color: 'primary.black',
                      mr: '4px'
                    }}
                  />
                  <Typography variant="body">
                    Add new medication
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: '16px'
                  }}
                >
                  <MedicationSelectForm
                    currentMedications={currentMedications}
                    setCurrentMedications={setCurrentMedications}
                    setShowMedicationSearchInput={setShowMedicationSearchInput}
                    onClose={handleMedicationSelectFormClose}
                  />
                </Box>
              )}
            </Box>
          </Box>
          {/* <Box
            sx={{
              width: '100%',
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0px 2px 10px 0px #5B87EF33',
              mt: '24px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderBottom: '1px solid',
                borderColor: 'primary.mediumGray',
                pb: '16px'
              }}
            >
              <Typography
                variant="h4"
              >
                Family groups
              </Typography>
              <Typography variant="subText">
                Select family groups that you would not want to set for others to view.
              </Typography>
            </Box>
            <FamilyGroupCheckboxList />
          </Box> */}
          {/* <Box
            sx={{
              width: '100%',
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: '0px 2px 10px 0px #5B87EF33',
              mt: '24px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderBottom: '1px solid',
                borderColor: 'primary.mediumGray',
                pb: '16px'
              }}
            >
              <Typography
                variant="h4"
              >
                Your order history
              </Typography>
            </Box>
            <Box
              sx={{
                mt: '16px'
              }}
            >
              <Box
                sx={{
                  padding: '12px',
                  borderRadius: '5px',
                  bgcolor: 'primary.lightBlue'
                }}
              >
                <Typography variant="body">Available information</Typography>
              </Box>
            </Box>
          </Box> */}
        </Box>
        <Box
          sx={{
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '1px solid',
            borderColor: 'primary.mediumGray'
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <BaseButton
                buttonType='default'
                sx={{
                  display: { xs: 'none', md: 'block' }
                }}
                onClick={onClose}
              >
                <Typography
                  variant="body"
                >
                  Cancel
                </Typography>
              </BaseButton>
            </Grid>
            <Grid item xs={12} md={6}>
              <BaseButton
                type="submit"
                buttonType='primary'
                loading={loading}
                onClick={handleSubmit}
              >
                <Typography
                  variant="body"
                  color='white'
                >
                  Save
                </Typography>
              </BaseButton>
            </Grid>
          </Grid>
        </Box>
      </form>
    </Box>
  )
}

export default ProfileEditForm